/**
 * Installing Zettl on a phone's home screen (public/app.webmanifest).
 *
 * - Chrome / Edge / Samsung on Android fire `beforeinstallprompt`: it is kept
 *   here so the in-app hint can open the browser's own install dialog.
 * - iOS Safari has no such event: the hint says where "Add to Home Screen" is.
 * - Once installed (standalone) or dismissed, the hint never shows again on
 *   this device (localStorage; a blocked storage just shows it per visit).
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const INSTALL_HINT_KEY = "zettl:install-hint-dismissed";

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Call once at start-up: the event fires only once per page load, often early. */
export function captureInstallPrompt(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    // Our hint offers it; no mini-infobar on top of it.
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    dismissInstallHint();
    notify();
  });
}

export function hasInstallPrompt(): boolean {
  return deferred !== null;
}

export function onInstallPromptChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Opens the browser's install dialog; true when the user installed. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null; // a prompt can be shown only once
  notify();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === "accepted";
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches;
}

/** iPhone / iPad Safari (iPadOS reports a Mac with touch). */
export function isIos(ua: string, maxTouchPoints: number): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
}

/** Only Safari can add to the home screen on iOS before 16.4; other iOS browsers don't say how. */
export function isIosSafari(ua: string, maxTouchPoints: number): boolean {
  return isIos(ua, maxTouchPoints) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function isInstallHintDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissInstallHint(): void {
  try {
    localStorage.setItem(INSTALL_HINT_KEY, "1");
  } catch {
    // Private mode: the hint comes back next visit, nothing breaks.
  }
}

export type InstallHintKind = "prompt" | "ios" | null;

/** Which hint to show, if any. */
export function installHintKind(state: {
  standalone: boolean;
  dismissed: boolean;
  hasPrompt: boolean;
  iosSafari: boolean;
}): InstallHintKind {
  if (state.standalone || state.dismissed) return null;
  if (state.hasPrompt) return "prompt";
  if (state.iosSafari) return "ios";
  return null;
}
